// SPDX-License-Identifier: BUSL-1.1

/**
 * pi.dev Agent detail page.
 *
 */

import { useParams, Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { apiFetch } from '@/lib/api-client';
import { DerivedAgentHealthBadge, PiDevAgentStatusBadge, PolicyGateBadge, BudgetUsageBar } from '@/components/badges';
import { HeartbeatHistoryTable } from '@/components/heartbeat-history-table';
import type { PiDevAgent, PiDevSession, PiDevBudget } from '@/types/api';

/* ── Session Row ─────────────────────────────── */

function SessionRow({ session }: { session: PiDevSession }) {
  const started = new Date(session.started_at);
  const ended = session.ended_at ? new Date(session.ended_at) : null;
  const duration = ended
    ? Math.round((ended.getTime() - started.getTime()) / 1000)
    : Math.round((Date.now() - started.getTime()) / 1000);
  
  const formatDuration = (s: number) => {
    if (s < 60) return `${s}s`;
    if (s < 3600) return `${Math.floor(s / 60)}m ${s % 60}s`;
    return `${Math.floor(s / 3600)}h ${Math.floor((s % 3600) / 60)}m`;
  };

  return (
    <tr className="border-t border-neutral-800">
      <td className="py-3 pr-4">
        <span className="font-mono text-xs text-neutral-400">
          {session.session_id.slice(0, 8)}...
        </span>
      </td>
      <td className="py-3 pr-4">
        <span className={`inline-flex rounded px-2 py-0.5 text-xs ${
          session.status === 'active'
            ? 'bg-green-900/30 text-green-400'
            : session.status === 'completed'
            ? 'bg-neutral-700 text-neutral-300'
            : 'bg-red-900/30 text-red-400'
        }`}>
          {session.status}
        </span>
      </td>
      <td className="py-3 pr-4 text-neutral-400">
        {started.toLocaleString()}
      </td>
      <td className="py-3 pr-4 text-neutral-400">
        {ended ? ended.toLocaleString() : '-'}
      </td>
      <td className="py-3 pr-4 font-mono text-xs text-neutral-400">
        {formatDuration(duration)}
      </td>
      <td className="py-3 pr-4 text-right font-mono text-xs text-neutral-400">
        ${session.cost.toFixed(4)}
      </td>
      <td className="py-3 text-right font-mono text-xs text-neutral-400">
        {session.tool_call_count}
      </td>
    </tr>
  );
}

/* ── Agent Detail ─────────────────────────────── */

function AgentDetail({ agentId }: { agentId: string }) {
  const agent = useQuery({
    queryKey: ['agents', 'pi-dev', agentId],
    queryFn: () => apiFetch<PiDevAgent>(`/pi-agents/${encodeURIComponent(agentId)}`),
    enabled: !!agentId,
  });

  const sessions = useQuery({
    queryKey: ['agents', 'pi-dev', agentId, 'sessions'],
    queryFn: () => apiFetch<{ data: PiDevSession[] }>(
      `/pi-agents/${encodeURIComponent(agentId)}/sessions`
    ),
    enabled: !!agentId,
  });

  const budget = useQuery({
    queryKey: ['agents', 'pi-dev', agentId, 'budget'],
    queryFn: () => apiFetch<PiDevBudget>(
      `/pi-agents/${encodeURIComponent(agentId)}/budget`
    ),
    enabled: !!agentId,
  });

  if (agent.isLoading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-32 rounded-lg bg-neutral-800" />
        <div className="h-64 rounded-lg bg-neutral-800" />
      </div>
    );
  }

  if (!agent.data) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
        <p className="text-neutral-400">Agent not found</p>
      </div>
    );
  }

  const agentData = agent.data;
  const sessionsData = sessions.data?.data ?? [];
  const budgetData = budget.data;

  return (
    <div className="space-y-6">
      {/* Agent Header */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-xl font-bold text-neutral-100">{agentData.name}</h2>
            <p className="mt-1 font-mono text-sm text-neutral-500">{agentData.agent_id}</p>
          </div>
          <div className="flex items-center gap-2">
            <PiDevAgentStatusBadge status={agentData.status} />
            <DerivedAgentHealthBadge
              agent={{
                status: agentData.status,
                lastHeartbeat: agentData.last_heartbeat,
              }}
            />
            <PolicyGateBadge gate={agentData.policy} />
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div>
            <p className="text-xs text-neutral-500">Source</p>
            <p className="mt-1 font-mono text-sm text-neutral-300">{agentData.source}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-500">Model</p>
            <p className="mt-1 text-sm text-neutral-300">{agentData.model ?? '-'}</p>
          </div>
          <div>
            <p className="text-xs text-neutral-500">Last Heartbeat</p>
            <p className="mt-1 text-sm text-neutral-300">
              {agentData.last_heartbeat
                ? new Date(agentData.last_heartbeat).toLocaleString()
                : 'Never'}
            </p>
          </div>
          <div>
            <p className="text-xs text-neutral-500">Registered</p>
            <p className="mt-1 text-sm text-neutral-300">
              {new Date(agentData.created_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      </div>

      {/* Budget */}
      {budgetData && (
        <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
          <h3 className="mb-4 text-lg font-semibold text-neutral-100">Budget</h3>
          <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
            <div>
              <p className="text-xs text-neutral-500">Used (Monthly)</p>
              <p className="mt-1 text-xl font-bold text-neutral-100">
                ${budgetData.used.toFixed(4)}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Limit</p>
              <p className="mt-1 text-xl font-bold text-neutral-100">
                {budgetData.limit > 0 ? `$${budgetData.limit}` : 'Unlimited'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Remaining</p>
              <p className="mt-1 text-xl font-bold text-neutral-100">
                {budgetData.remaining !== null ? `$${budgetData.remaining.toFixed(4)}` : '-'}
              </p>
            </div>
            <div>
              <p className="text-xs text-neutral-500">Usage</p>
              <div className="mt-2">
                <BudgetUsageBar
                  used={budgetData.used}
                  limit={budgetData.limit}
                  percentUsed={budgetData.percent_used}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Heartbeats (HB-07) */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
        <h3 className="mb-4 text-lg font-semibold text-neutral-100">Heartbeats</h3>
        <HeartbeatHistoryTable agentId={agentId} />
      </div>

      {/* Sessions */}
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-6">
        <h3 className="mb-4 text-lg font-semibold text-neutral-100">
          Sessions ({sessionsData.length})
        </h3>
        
        {sessionsData.length === 0 ? (
          <p className="py-4 text-center text-neutral-500">No sessions yet</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-neutral-500">
                  <th className="pb-2 pr-4">Session ID</th>
                  <th className="pb-2 pr-4">Status</th>
                  <th className="pb-2 pr-4">Started</th>
                  <th className="pb-2 pr-4">Ended</th>
                  <th className="pb-2 pr-4">Duration</th>
                  <th className="pb-2 pr-4 text-right">Cost</th>
                  <th className="pb-2 text-right">Tool Calls</th>
                </tr>
              </thead>
              <tbody className="text-neutral-300">
                {sessionsData.slice(0, 20).map((session) => (
                  <SessionRow key={session.id} session={session} />
                ))}
              </tbody>
            </table>
            {sessionsData.length > 20 && (
              <p className="mt-4 text-center text-xs text-neutral-500">
                Showing 20 of {sessionsData.length} sessions
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Page ──────────────────────────────────── */

export function PiDevAgentDetailPage() {
  const { id } = useParams<{ id: string }>();

  if (!id) {
    return (
      <div className="rounded-lg border border-neutral-800 bg-neutral-900 p-8 text-center">
        <p className="text-neutral-400">Agent ID not provided</p>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <Link to="/agents" className="text-sm text-blue-400 hover:underline">
          ← Back to Agents
        </Link>
      </div>
      
      <AgentDetail agentId={id} />
    </div>
  );
}
